import { useState } from 'react';
import { Button } from '../../../../../components/Button';
import { DISTRIBUTION_COMPLETED, distributeEvent } from '../../../../../services/calendar';
import { getSafeErrorMessage } from '../../../../../utils/errorHandler';
import type { ItineraryRequestLaunch } from '../itinerarySetup';

interface ShareWithProgramStepProps {
  /** The tournament just published, in RosterPilot's own organization. */
  calendarEventId: string;
  /** The final tournament name, so the confirmation names what is actually being sent. */
  tournamentName: string;
  launch: ItineraryRequestLaunch;
  /** Leave the wizard without sharing — the tournament stays built and the request stays open. */
  onSkip: () => void;
  /** The share landed AND the program's request closed. Leaves the wizard. */
  onShared: () => void;
}

/**
 * The last step when RosterPilot staff build an itinerary a program asked for: send it to them.
 *
 * This exists because building and delivering were two unconnected screens. Staff finished the
 * wizard, which left the tournament on RosterPilot's OWN calendar, then had to find it again on the
 * schedule, open it, scroll to the admin block, and search the recipient out of a list of every
 * organization — with nothing anywhere reminding them which program had asked. The queue row stayed
 * Pending until someone remembered to come back to it.
 *
 * <b>Sharing is what fulfils the request</b>, not a separate bookkeeping call: distribution puts a
 * copy on the program's calendar and closes their queue row in the same transaction-per-org. The
 * request id travels with it so the row closes by IDENTITY rather than by tournament-name matching,
 * which is exact and would miss the moment anyone renamed the tournament while building it — which
 * this wizard actively invites.
 */
export function ShareWithProgramStep({
  calendarEventId,
  tournamentName,
  launch,
  onSkip,
  onShared,
}: ShareWithProgramStepProps) {
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState('');
  // Delivered, but the queue row did not close. Not an error — the program HAS the itinerary — so
  // it must not read like one, and it must not silently navigate away either: closing the row is a
  // separate best-effort step server-side, and this is the only moment anyone is in a position to
  // notice it did not happen.
  const [sharedButOpen, setSharedButOpen] = useState(false);

  const share = async () => {
    setSharing(true);
    setError('');
    try {
      const result = await distributeEvent(
        calendarEventId,
        { allOrganizations: false, organizationIds: [launch.organizationId] },
        launch.requestId,
      );

      // Resolved BY ORGANIZATION, and required to be `completed`. A per-org outcome is reported in
      // the body rather than thrown, and "no failures" is not the same as "it arrived": the server
      // buckets everything that is neither `completed` nor `failed` as *skipped*, so a recipient
      // who had previously deleted their copy comes back `skipped_deleted` — no copy written, no
      // fulfilment attempted, `failed: 0`. Reading only `failed` navigated away from that with a
      // success screen, which is the exact outcome this whole flow exists to prevent. Resolving to
      // zero target orgs (`results: []`) is the same bug in a second shape.
      const outcome = result.results?.find(
        (r) => r.organizationId === launch.organizationId,
      );

      if (outcome?.status !== DISTRIBUTION_COMPLETED) {
        setError(
          outcome?.error ||
            `We couldn't share the itinerary with ${launch.organizationName}. ` +
              "Check you are not building it inside that program's own organization, then try again.",
        );
        return;
      }

      // Shared for certain; the request is a separate best-effort step server-side, so report which
      // actually happened rather than implying both.
      if (outcome.fulfilledItineraryRequestId == null) {
        setSharedButOpen(true);
        return;
      }

      onShared();
    } catch (err) {
      setError(
        getSafeErrorMessage(
          err,
          `We couldn't share the itinerary with ${launch.organizationName}. Try again.`,
        ),
      );
    } finally {
      setSharing(false);
    }
  };

  return (
    <section className="wiz-share" data-testid="wizard-share-step">
      <h2 className="wiz-share__title">Send it to {launch.organizationName}</h2>
      <p className="wiz-share__lede">
        <strong>{tournamentName}</strong> is built and on RosterPilot&rsquo;s calendar. Sharing it
        puts a copy on {launch.organizationName}&rsquo;s calendar, notifies their staff, and closes
        the request they raised.
      </p>

      <dl className="wiz-share__facts">
        <div className="wiz-share__fact">
          <dt>They asked for</dt>
          <dd>{launch.tournamentName}</dd>
        </div>
        <div className="wiz-share__fact">
          <dt>You built</dt>
          <dd>{tournamentName}</dd>
        </div>
      </dl>

      {launch.tournamentName.trim().toLowerCase() !== tournamentName.trim().toLowerCase() && (
        // Worth saying out loud rather than hiding: the names differing is fine — the request is
        // closed by id — but staff should see that the program will receive a differently-titled
        // tournament from the one they asked for.
        <p className="wiz-share__note" data-testid="wizard-share-renamed">
          The name changed while you were building it. {launch.organizationName} will see the name
          you built, and their request will still be marked fulfilled.
        </p>
      )}

      {error && (
        <div className="wiz-share__error" role="alert" data-testid="wizard-share-error">
          {error}
        </div>
      )}

      {sharedButOpen && (
        <div className="wiz-share__note" role="status" data-testid="wizard-share-still-open">
          Shared with {launch.organizationName}. Their request did not close by itself, so it is
          still in the queue — mark it fulfilled there once you have checked they received it.
        </div>
      )}

      <div className="wiz-share__actions">
        {sharedButOpen ? (
          <Button
            type="button"
            variant="primary"
            onClick={onShared}
            data-testid="wizard-share-to-queue"
          >
            Go to the queue
          </Button>
        ) : (
          <>
            <Button type="button" variant="secondary" onClick={onSkip} disabled={sharing}>
              Not now
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => void share()}
              disabled={sharing}
              data-testid="wizard-share-confirm"
            >
              {sharing ? 'Sharing…' : `Share with ${launch.organizationName}`}
            </Button>
          </>
        )}
      </div>

      <p className="wiz-share__hint">
        Not now leaves the itinerary built on RosterPilot&rsquo;s calendar and the request open — you
        can share it later from the event itself.
      </p>
    </section>
  );
}
